import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Card, Badge, EmptyState } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

const emptyForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  department_id: "", member_id: "", name: "",
  assessment_type: "Knowledge", score: 0, pass_threshold: 60,
  result: "", retake_date: "", notes: "",
});

export default function Assessments() {
  const { departments, deptName, selectedDept } = useApp();
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [allMembers, setAllMembers] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get("/assessments", { params: { department_id: selectedDept !== "all" ? selectedDept : undefined } })
      .then((r) => setItems(r.data)).catch(() => {});
    api.get("/team-members").then((r) => {
      const map = {}; r.data.forEach((m) => (map[m.id] = m)); setAllMembers(map);
    });
  }, [selectedDept]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!form.department_id) { setMembers([]); return; }
    api.get("/team-members", { params: { department_id: form.department_id } }).then((r) => setMembers(r.data));
  }, [form.department_id]);

  const openNew = () => { setForm(emptyForm()); setEditId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...emptyForm(), ...a, retake_date: a.retake_date || "", result: a.result || "" }); setEditId(a.id); setOpen(true); };

  const save = async () => {
    if (!form.department_id || !form.member_id || !form.name) { toast.error("Department, member and name are required."); return; }
    const payload = {
      ...form, score: Number(form.score), pass_threshold: Number(form.pass_threshold),
      result: form.result || null, retake_date: form.retake_date || null,
    };
    try {
      if (editId) await api.put(`/assessments/${editId}`, payload);
      else await api.post("/assessments", payload);
      toast.success(editId ? "Assessment updated." : "Assessment recorded.");
      setOpen(false); load();
    } catch (e) { toast.error("Failed to save."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this assessment record?")) return;
    await api.delete(`/assessments/${id}`); toast.success("Deleted."); load();
  };

  const resultTone = (r) => (r === "Pass" ? "green" : r === "Fail" ? "red" : "amber");

  return (
    <div>
      <PageHeader
        title="Assessment & Test Tracker"
        subtitle="Knowledge tests and situational / scenario-based assessments."
        action={<Button data-testid="add-assessment-button" onClick={openNew} className="bg-[#C9A24B] text-[#0B1120] hover:bg-[#b8923f] gap-2"><Plus className="h-4 w-4" /> Record Assessment</Button>}
      />
      <Card className="p-0 overflow-hidden">
        {items.length === 0 ? (
          <EmptyState icon={FileCheck2} title="No assessments" hint="Record a knowledge test or situational scenario." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead>Date</TableHead><TableHead>Member</TableHead><TableHead>Department</TableHead>
                <TableHead>Assessment</TableHead><TableHead>Type</TableHead><TableHead>Score</TableHead>
                <TableHead>Result</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id} data-testid={`assessment-row-${a.id}`} className="border-slate-800 hover:bg-slate-800/40">
                  <TableCell className="font-mono text-slate-300">{a.date}</TableCell>
                  <TableCell className="text-slate-300">{allMembers[a.member_id]?.name || "—"}</TableCell>
                  <TableCell className="text-slate-400 text-sm">{deptName(a.department_id)}</TableCell>
                  <TableCell className="text-slate-300 text-sm">{a.name}</TableCell>
                  <TableCell><Badge tone="slate">{a.assessment_type}</Badge></TableCell>
                  <TableCell className="font-mono text-white">{a.score}<span className="text-slate-600 text-xs">/{a.pass_threshold}</span></TableCell>
                  <TableCell><Badge tone={resultTone(a.result)}>{a.result}</Badge></TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => openEdit(a)} className="text-slate-400 hover:text-[#C9A24B] mr-3" data-testid={`edit-assessment-${a.id}`}><Pencil className="h-4 w-4 inline" /></button>
                    <button onClick={() => remove(a.id)} className="text-slate-400 hover:text-[#DC2626]" data-testid={`delete-assessment-${a.id}`}><Trash2 className="h-4 w-4 inline" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F172A] border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit Assessment" : "Record Assessment"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="border-slate-700" data-testid="assessment-date" /></div>
              <div>
                <Label>Type</Label>
                <Select value={form.assessment_type} onValueChange={(v) => setForm({ ...form, assessment_type: v })}>
                  <SelectTrigger className="border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Knowledge">Knowledge</SelectItem><SelectItem value="Situational">Situational</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Department</Label>
                <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v, member_id: "" })}>
                  <SelectTrigger data-testid="assessment-department" className="border-slate-700"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Member</Label>
                <Select value={form.member_id} onValueChange={(v) => setForm({ ...form, member_id: v })}>
                  <SelectTrigger data-testid="assessment-member" className="border-slate-700"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Assessment Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Objection Handling Roleplay" className="border-slate-700" data-testid="assessment-name" /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Score</Label><Input type="number" value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })} className="border-slate-700" data-testid="assessment-score" /></div>
              <div><Label>Pass Threshold</Label><Input type="number" value={form.pass_threshold} onChange={(e) => setForm({ ...form, pass_threshold: e.target.value })} className="border-slate-700" /></div>
              <div>
                <Label>Result</Label>
                <Select value={form.result || "auto"} onValueChange={(v) => setForm({ ...form, result: v === "auto" ? "" : v })}>
                  <SelectTrigger className="border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="Pass">Pass</SelectItem>
                    <SelectItem value="Fail">Fail</SelectItem>
                    <SelectItem value="Needs Retake">Needs Retake</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Retake Date (if applicable)</Label><Input type="date" value={form.retake_date} onChange={(e) => setForm({ ...form, retake_date: e.target.value })} className="border-slate-700" /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-slate-700" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-assessment-button" onClick={save} className="bg-[#C9A24B] text-[#0B1120] hover:bg-[#b8923f]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
